import { observable, action, computed } from "mobx"
import axios from "axios"
import _ from "lodash"
import { logEvent } from "../utils/analytics"

import { MINIMUM_TAG_LIST_LENGTH, createSampleEventApiUrl } from "../utils/samplefocusDefaults"
import { getCsrfToken } from "../utils/userUtils"

export default class Sample {
  @observable favorites_count
  @observable tag_list
  @observable cyaniteTags
  @observable name
  @observable key
  @observable tempo
  @observable mode
  @observable category_id
  @observable touchedFields = {}
  @observable isFavorited
  @observable isDownloaded
  @observable isSelected
  @observable isSelectedForSequencer
  @observable isPlaying
  @observable enabledForPlay
  @observable shouldBePublished
  @observable suggestedName

  constructor(sampleObject) {
    const o = sampleObject
    this.csrfToken = getCsrfToken()
    this.assigned_color = o.assigned_color ? o.assigned_color : "#64b5f6"
    this.category_id = o.category_id ? o.category_id : null
    this.created_at = new Date(o.created_at)
    this.duration = Number.parseFloat(o.duration)
    this.id = o.id
    this.favorites_count = o.favorites_count || 0
    this.mode = o.mode
    this.key = o.key
    this.name = o.name ? o.name : ""
    this.published_at = o.published_at ? new Date(o.published_at) : null
    this.shouldBePublished = Boolean(this.published_at)
    this.sample_mp3_url = o.sample_mp3_url
    this.api_sample_url = o.api_sample_url
    this.sample_url = o.sample_url
    this.sample_original_filename = o.sample_original_filename
    this.sample_waveform_url = o.sample_waveform_url
    this.slug = o.slug
    this.tag_list = o.tag_list
    this.sample_tags = o.sample_tags
    this.tempo = o.tempo
    this.user_avatar_url = o.user_avatar_url
    this.user_display_name_truncated = o.user_display_name_truncated
    this.user_slug = o.user_slug
    this.events = o.events
    this.isDownloaded = false
    this.isSelected = o.isSelected ? Boolean(o.isSelected) : false
    this.isSelectedForSequencer = o.isSelectedForSequencer ? Boolean(o.isSelectedForSequencer) : false
    this.isFavorited = false
    this.isPlaying = false
    this.enabledForPlay = true
    this.cyaniteTags = o.cyanite_tags ? o.cyanite_tags : {}
  }

  static FIELDS_TO_VALIDATE = ["name", "tempo", "tag_list", "category_id", "loop_or_one_shot"]

  @computed
  get displayName() {
    return this.name ? this.name : this.sample_original_filename
  }

  @computed
  get enabledForSuggestedName() {
    return !_.isEmpty(this.cyaniteTags) || this.tag_list.length > 0
  }

  @action
  addTag = (tag) => {
    if (!this.tag_list.includes(tag)) {
      this.tag_list.push(tag)
    }
  }

  @action
  removeTag = (tag) => {
    this.tag_list = this.tag_list.filter((t) => t !== tag)
  }

  @action
  removeCyaniteTag = (tag) => {
    let filteredCyaniteTags = _.mapValues(this.cyaniteTags, function(cyaniteTagsByType) {
      return cyaniteTagsByType.filter((t) => t !== tag)
    })
    this.cyaniteTags = filteredCyaniteTags
  }

  @action
  clearTags = () => {
    this.tag_list = []
  }

  @action
  select = () => {
    this.isSelected = true
  }

  @action
  selectForSequencer = () => {
    this.isSelectedForSequencer = true
  }


  @action
  deselect = () => {
    this.isSelected = false
  }

  @action
  deselectForSequencer = () => {
    this.isSelectedForSequencer = false
  }

  @action
  setSuggestedName = (suggestedName) => {
    this.suggestedName = suggestedName
  }

  @action
  setName = (name) => {
    this.name = name
  }

  @action
  setToPlay = () => {
    this.isPlaying = true
  }

  @action
  setToStop = () => {
    this.isPlaying = false
  }

  @action
  setToFavorite = () => {
    this.isFavorited = true
  }

  @action
  setToDownloaded = () => {
    this.isDownloaded = true
  }

  @action
  setIfPublished = (shouldBePublished) => {
    this.shouldBePublished = shouldBePublished
  }

  @computed
  get sortedTags() {
    const tagListCopy = this.tag_list
    return tagListCopy.sort()
  }

  @action
  touchAllFields = () => {
    const fieldsToTouch = Sample.FIELDS_TO_VALIDATE
    fieldsToTouch.forEach((fieldName) => {
      this.touchField(fieldName)
    })
  }

  @action
  touchField = (field) => {
    this.touchedFields[field] = true
  }

  @action
  toggleEnabledForPlay = () => {
    this.enabledForPlay = !this.enabledForPlay
  }

  @computed
  get validationErrors() {
    const errors = {}
    if (_.isEmpty(this.name)) {
      errors["name"] = true
    }

    if (!_.isEmpty(this.tempo) && this.tempo <= 0) {
      errors["tempo"] = true
    }

    if (_.isEmpty(this.key) && !_.isEmpty(this.mode)) {
      errors["mode_but_not_key_present"] = true
    }

    if (this.tag_list.length < MINIMUM_TAG_LIST_LENGTH) {
      errors["tag_list"] = true
    }

    const isTaggedEitherLoopOrOneshot = this.tag_list.includes("loop") || this.tag_list.includes("one shot")
    if (!isTaggedEitherLoopOrOneshot) {
      errors["loop_or_one_shot"] = true
    }

    if (this.category_id == null) {
      errors["category_id"] = true
    }

    return errors
  }

  async save() {
    const errors = this.validationErrors
    if (!_.isEmpty(errors)) {
      console.log("Error on save", errors)
      return false
    }

    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        method: "patch",
        url: this.api_sample_url,
        timeout: 25000,
        data: {
          sample: {
            category_id: this.category_id,
            name: this.name,
            tag_list: this.tag_list,
            tempo: this.tempo,
            key: this.key,
            mode: this.mode,
            publish: this.shouldBePublished,
          },
          authenticity_token: this.csrfToken,
        },
      })
      console.log("response", response)
      return true
    } catch (error) {
      console.error("save error", error)
      return false
    }
  }

  async delete() {
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        method: "delete",
        url: this.api_sample_url,
        timeout: 4000,
        data: {
          sample: {
            id: this.id,
          },
          authenticity_token: this.csrfToken,
        },
      })
      console.log("response", response)
      return true
    } catch (error) {
      console.error("save error", error)
      return false
    }
  }

  async favorite() {
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        method: "post",
        url: "/favorites",
        timeout: 25000,
        data: {
          favoritable_id: this.id,
          favoritable_type: 'Sample',
          authenticity_token: this.csrfToken,
        },
      })

      logEvent("Favorited sample")
      this.favorites_count += 1
      this.isFavorited = true
      return true
    } catch (error) {
      console.error("save error", error)
      return false
    }
  }

  async unfavorite() {
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        method: "delete",
        url: "/favorites/" + this.id,
        timeout: 25000,
        data: {
          favoritable_type: 'Sample',
          authenticity_token: this.csrfToken,
        },
      })

      this.favorites_count -= 1
      this.isFavorited = false
      return true
    } catch (error) {
      console.error("save error", error)
      return false
    }
  }

  async createEvent(eventKind, reasons) {
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        method: "post",
        url: createSampleEventApiUrl,
        timeout: 25000,
        data: {
          sample_id: this.id,
          state: eventKind,
          reasons: reasons
        }
      })

      return true
    } catch (error) {
      console.error("create event error", error)
      return false
    }
  }

  createPlay = async () => {
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        method: "post",
        url: "/plays",
        timeout: 5000,
        data: {
          sample_id: this.id,
          authenticity_token: this.csrfToken
        }
      })
      return true
    } catch (error) {
      console.error("create play error", error)
      return false
    }
  }

  getSuggestedName = async ({setNameToSuggested = false}) => {
    axios.defaults.headers.common["Accept"] = "application/json"
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken

    try {
      const response = await axios({
        url: this.api_sample_url + "/get_suggested_name",
        responseType: "json",
        params: {
          tags: this.tag_list.join(','),
          category_id: this.category_id
        }
      })

      const suggestedName = response.data.suggested_name
      this.setSuggestedName(suggestedName)
      if (setNameToSuggested) {
        this.setName(suggestedName)
      }
      return true
    } catch (error) {
      console.error("getSuggestedNames error", error)
      return false
    }
  }

  toJSON() {
    return {
      id: this.id,
      sample_mp3_url: this.sample_mp3_url,
      key: this.key,
      mode: this.mode,
      slug: this.slug,
      name: this.name,
      tempo: this.tempo,
      category_id: this.category_id,
      isSelectedForSequencer: this.isSelectedForSequencer,
      tag_list: this.tag_list,
      duration: this.duration,
      // Include other properties you need to persist
    };
  }

}